import type React from "react";
import { House } from "@phosphor-icons/react/House";
import { CalendarBlank } from "@phosphor-icons/react/CalendarBlank";
import { CreditCard } from "@phosphor-icons/react/CreditCard";
import { Users } from "@phosphor-icons/react/Users";
import { Megaphone } from "@phosphor-icons/react/Megaphone";
import { ChatCircle } from "@phosphor-icons/react/ChatCircle";
import { UserCircle } from "@phosphor-icons/react/UserCircle";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { CalendarCheck } from "@phosphor-icons/react/CalendarCheck";
import { ClipboardText } from "@phosphor-icons/react/ClipboardText";
import { FileText } from "@phosphor-icons/react/FileText";
import { Bell } from "@phosphor-icons/react/Bell";
import { GearSix } from "@phosphor-icons/react/GearSix";
import { BookOpenText } from "@phosphor-icons/react/BookOpenText";
import { BookmarkSimple } from "@phosphor-icons/react/BookmarkSimple";
import { HandHeart } from "@phosphor-icons/react/HandHeart";
import {
  MRHome, MRAnnouncements, MRProfile, MRHousehold, MRDirectory, MRGiving,
  MRGroups, MRMessages, MREvents, MRRegistrations, MRAttendance, MRPledges,
  MRRequests, MRPrayer, MRMedia, MRNotifications, MRPreferences, MRSchedule,
  MRAvailability,
} from "@/components/icons/MemberIcons";
import { isPortalVisible } from "@/lib/capabilities/portal";
import { CAPABILITIES } from "@/lib/capabilities/registry";
import type { Capability, ResolveContext } from "@/lib/capabilities/types";

export interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ weight?: string; className?: string }>;
  /** Photo-real 64×64 SVG icon for "More" sheet tiles */
  prIcon?: React.ComponentType<React.ComponentPropsWithoutRef<"svg">>;
  section?: string;
  /** Grouping label for the "More" sheet */
  moreGroup?: string;
  colorKey: string;
  /**
   * Capability key gating this entry. When set, the entry is shown only when
   * the capability resolves available AND its registry `portal.visible` is not
   * false. Entries without a `capability` always show.
   */
  capability?: string;
}
export const BASE_NAV: NavItem[] = [
  { label: "Overview",       href: "/member",                  icon: House,          prIcon: MRHome,            colorKey: "home",          moreGroup: "Connect" },
  { label: "Announcements",  href: "/member/announcements",    icon: Megaphone,      prIcon: MRAnnouncements,   colorKey: "announcements", moreGroup: "Connect" },
  { label: "Profile",        href: "/member/profile",          icon: UserCircle,     prIcon: MRProfile,         colorKey: "profile",       moreGroup: "My Account" },
  { label: "Household",      href: "/member/household",        icon: UsersThree,     prIcon: MRHousehold,       colorKey: "household",     moreGroup: "My Account" },
  { label: "Directory",      href: "/member/directory",        icon: Users,          prIcon: MRDirectory,       colorKey: "directory",     moreGroup: "Connect", capability: "member_directory" },
  // Giving (history + statements) is gated by the dedicated `allow_giving_statements`
  // portal setting via applyGivingStatementsGate — NOT the `online_giving` payments
  // capability — so payments-off / free-tier churches keep their statements. (GIV-5)
  { label: "Giving",         href: "/member/giving",           icon: CreditCard,     prIcon: MRGiving,          colorKey: "giving",        moreGroup: "Give" },
  { label: "Groups",         href: "/member/groups",           icon: UsersThree,     prIcon: MRGroups,          colorKey: "groups",        moreGroup: "Connect", capability: "groups" },
  { label: "Messages",       href: "/member/messages",         icon: ChatCircle,     prIcon: MRMessages,        colorKey: "messages",      moreGroup: "Connect" },
  { label: "Events",         href: "/member/events",           icon: CalendarBlank,  prIcon: MREvents,          colorKey: "events",        moreGroup: "Connect" },
  { label: "Registrations",  href: "/member/registrations",    icon: FileText,       prIcon: MRRegistrations,   colorKey: "registrations", moreGroup: "Connect" },
  { label: "Attendance",     href: "/member/attendance",       icon: CalendarCheck,  prIcon: MRAttendance,      colorKey: "attendance",    moreGroup: "Connect" },
  { label: "Pledges",        href: "/member/pledges",          icon: BookmarkSimple, prIcon: MRPledges,         colorKey: "pledges",       moreGroup: "Give",    capability: "pledge_management" },
  { label: "Requests",       href: "/member/requests",         icon: ClipboardText,  prIcon: MRRequests,        colorKey: "requests",      moreGroup: "Serve" },
  { label: "Prayer Wall",    href: "/member/prayer",           icon: HandHeart,      prIcon: MRPrayer,          colorKey: "prayer",        moreGroup: "Connect", capability: "prayer_wall" },
  { label: "Media",          href: "/member/media",            icon: BookOpenText,   prIcon: MRMedia,           colorKey: "media",         moreGroup: "Connect" },
  { label: "Notifications",  href: "/member/notifications",    icon: Bell,           prIcon: MRNotifications,   colorKey: "notifications", moreGroup: "My Account" },
  { label: "Kids & Youth",   href: "/member/family/preferences", icon: Bell,         prIcon: MRNotifications,   colorKey: "notifications", moreGroup: "My Account", capability: "kids_checkin" },
  { label: "School Reports", href: "/member/family/daily-reports", icon: FileText,   prIcon: MRAttendance,      colorKey: "attendance",    moreGroup: "My Account", capability: "kids_checkin" },
  { label: "Preferences",    href: "/member/preferences",      icon: GearSix,        prIcon: MRPreferences,     colorKey: "preferences",   moreGroup: "My Account" },
];

const VOLUNTEER_NAV: NavItem[] = [
  { label: "Serving Schedule", href: "/member/schedule",     icon: CalendarCheck, prIcon: MRSchedule,     section: "Volunteering", colorKey: "schedule",     moreGroup: "Serve" },
  { label: "Availability",     href: "/member/availability", icon: HandHeart,     prIcon: MRAvailability, section: "Volunteering", colorKey: "availability", moreGroup: "Serve" },
];

export function buildNavigation(isVolunteer: boolean): NavItem[] {
  if (!isVolunteer) return BASE_NAV;
  const eventsIdx = BASE_NAV.findIndex((item) => item.href === "/member/events");
  const insertAt = eventsIdx >= 0 ? eventsIdx + 1 : BASE_NAV.length;
  return [...BASE_NAV.slice(0, insertAt), ...VOLUNTEER_NAV, ...BASE_NAV.slice(insertAt)];
}

/**
 * Filter member nav items by the capability system. An item that declares a
 * `capability` is kept only when BOTH:
 *   1. the capability resolves `available === true` (role/tier/toggle/flag), AND
 *   2. its registry `portal.visible` is not explicitly false.
 * Items without a `capability` always pass through.
 *
 * Pure function — exported for direct unit testing so production and tests
 * exercise the same gating logic. The optional `registry` parameter (mirroring
 * `resolveCapability`'s signature) controls both the `portal.visible` lookup
 * and the resolution itself, so a test fixture can fully drive gating without
 * the live CAPABILITIES registry. Production callers omit it.
 */
export function filterMemberNav(
  items: NavItem[],
  resolveCtx: ResolveContext,
  registry: Record<string, Capability> = CAPABILITIES,
): NavItem[] {
  return items.filter((item) => {
    if (!item.capability) return true;
    return isPortalVisible(item.capability, resolveCtx, registry);
  });
}

/**
 * Gate the member "Giving" entry on the dedicated `allow_giving_statements`
 * portal setting — the SAME signal `MemberGivingStatements` enforces in-page —
 * rather than the `online_giving` PAYMENTS capability. Read-only giving history
 * and tax statements must stay reachable for payments-off / free-tier churches
 * (manual/imported gifts still produce statements). Only an explicit `false`
 * hides the entry; `undefined` (settings still loading / unset) keeps it visible
 * so it never flickers out during load. Pure + exported for direct unit testing.
 * (GIV-5)
 */
export function applyGivingStatementsGate(
  items: NavItem[],
  allowGivingStatements: boolean | undefined,
): NavItem[] {
  if (allowGivingStatements === false) {
    return items.filter((item) => item.href !== "/member/giving");
  }
  return items;
}
